import React from 'react';
import { InsightRendererProps } from '@ecosystem/newron-client-collections/build/shared/types';

import { CorePageProvider } from '../../page/provider';
import { DataProviderElement } from '../../data/provider';
import { InsightCollectionDataType, InsightData } from './types';
import { InsightElementWithData } from './insightElementWithData';
import { AnalyticsProvider } from '../../analytics/analytics.provider';
import { getTrackingTagsFn, mapCoreMetaParams, mapTabName } from '../../analytics/tools';
import { useTracking } from '../../analytics/tracking.hook';
import { useParams } from 'react-router';
import { isBulkExportPage, reformatCorePageSettings, reformatCustomFeatureTiers } from './utils';

export const InsightElement = ({
  insight: { data: insightData, dataType, type, position, proposition, viewKey },
  onLoaded,
  onExportReady,
  onError,
  onDataChanged,
}: InsightRendererProps<InsightData>) => {
  const isBulkExport = isBulkExportPage();

  const reformatHostInsightData: InsightData = { ...reformatCorePageSettings(insightData) };
  const sanitizedInsightData: InsightData = {
    ...reformatHostInsightData,
    paramsForData: reformatCustomFeatureTiers(reformatHostInsightData.paramsForData),
    componentSettings: reformatCustomFeatureTiers(reformatHostInsightData.componentSettings),
  };
  const { paramsForData, corePageSettings, content, paramsForContext } = sanitizedInsightData;
  const { page, category } = useParams();
  const calcTrackingParams = (_eventName, meta) => {
    const subsection = meta.pageTitle || category;
    const pageName = `${subsection} ${mapTabName(page)}`;
    return mapCoreMetaParams({ proposition, meta, subsection, pageName });
  };

  const { trackEvent } = useTracking(calcTrackingParams);
  const getTrackingTags = getTrackingTagsFn(category, page, false, calcTrackingParams);
  const collectionProps = { onLoaded, onExportReady, onError };
  const componentName = type + (position || '');

  return (
    <CorePageProvider
      {...corePageSettings}
      collection={{
        ...corePageSettings.collection,
        isLive: dataType === InsightCollectionDataType.live,
        params: paramsForContext,
        insightData: sanitizedInsightData,
        onDataChanged,
        onError,
        viewKey,
      }}
      isBulkExport={isBulkExport}
    >
      <AnalyticsProvider externalTrackingFn={trackEvent} externalGetTrackingTags={getTrackingTags}>
        <DataProviderElement
          dataOptions={content.dataOptions}
          paramsForData={paramsForData}
          urlRoot={corePageSettings.data.url}
          collection={{
            isLive: dataType === InsightCollectionDataType.live,
            onError,
          }}
          isBulkExport={isBulkExport}
        >
          <InsightElementWithData
            {...sanitizedInsightData}
            collection={collectionProps}
            componentName={componentName}
          />
        </DataProviderElement>
      </AnalyticsProvider>
    </CorePageProvider>
  );
};
